import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderKanban, RotateCcw, Trash2 } from 'lucide-react';
import { ContentChannel, TRASH_RETENTION_DAYS, type TrashItem } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useTenantKey } from '../../lib/useTenantKey';
import { cn } from '../../lib/cn';
import { formatRelative } from '../../lib/display';
import { Button } from '../../components/ui/Button';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { useDialogs } from '../../components/ui/DialogProvider';
import { postTypeIcon } from './postTypes';
import { PlatformIcon } from './PlatformIcon';
import { PostCreatorAvatar } from './PostCreatorAvatar';
import { CategoryPill } from '../categories/CategoryPill';
import { invalidateContentQueries } from '../../lib/contentCache';

/** Stable selection key — kind + id, since campaigns and posts are separate id spaces. */
const keyOf = (i: TrashItem) => `${i.kind}:${i.id}`;

/** Whole days left before an item is purged; 0 means it goes in the next sweep. */
function daysLeft(purgeAt: string): number {
  return Math.max(0, Math.ceil((Date.parse(purgeAt) - Date.now()) / 86_400_000));
}

/**
 * The Trash: deleted Campaigns and Posts, held for {@link TRASH_RETENTION_DAYS} days before being
 * cleared automatically. Items restore to exactly where they were, or can be destroyed early.
 *
 * Posts trashed WITH their campaign are deliberately not listed on their own — the campaign row
 * counts them and they return with it, so listing them separately would imply a restore that isn't
 * theirs to make.
 */
export function TrashView() {
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const { confirm, alert } = useDialogs();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const query = useQuery({
    queryKey: ['trash', tenantKey],
    queryFn: ({ signal }) => api.getTrash(signal),
    enabled: tenantKey !== '',
  });
  const items = useMemo(() => query.data ?? [], [query.data]);

  // Intersect rather than trusting the raw set: a refetch (or a restore in another tab) can drop
  // rows, and the action bar must never count items that are no longer there.
  const selectedItems = items.filter((i) => selected.has(keyOf(i)));
  const allSelected = items.length > 0 && selectedItems.length === items.length;

  const invalidate = () => {
    setSelected(new Set());
    // Includes ['campaign', tenantKey]: a restored post belongs to a campaign whose detail view may
    // already be warm in the cache, and without this it comes back everywhere EXCEPT the campaign
    // it was restored into.
    invalidateContentQueries(queryClient, tenantKey);
  };
  const onError = (verb: string) => (err: unknown) =>
    void alert({
      title: `Could not ${verb}`,
      message: err instanceof ApiRequestError ? err.message : 'Please try again.',
    });

  const split = (list: TrashItem[]) => ({
    campaignIds: list.filter((i) => i.kind === 'campaign').map((i) => i.id),
    postIds: list.filter((i) => i.kind === 'post').map((i) => i.id),
  });

  const restore = useMutation({
    mutationFn: (list: TrashItem[]) => api.restoreTrash(split(list)),
    onSuccess: invalidate,
    onError: onError('restore'),
  });
  const purge = useMutation({
    mutationFn: (list: TrashItem[]) => api.purgeTrash(split(list)),
    onSuccess: invalidate,
    onError: onError('delete'),
  });

  async function askPurge(list: TrashItem[]) {
    if (list.length === 0) return;
    const campaigns = list.filter((i) => i.kind === 'campaign').length;
    // purgePostCount, not postCount: deleting a campaign forever destroys EVERY post it owns, not
    // just the ones that would come back on a restore. Quoting the smaller number would understate
    // what's about to be lost.
    const withPosts = list
      .filter((i) => i.kind === 'campaign')
      .reduce((n, i) => n + (i.purgePostCount ?? i.postCount ?? 0), 0);
    const many = list.length !== 1;
    if (
      await confirm({
        title: many ? `Delete ${list.length} items forever` : 'Delete forever',
        message:
          `This permanently removes ${many ? `${list.length} items` : 'this item'} from the system and cannot be undone.` +
          (campaigns > 0 && withPosts > 0
            ? ` The ${withPosts} post${withPosts === 1 ? '' : 's'} inside the selected campaign${campaigns === 1 ? '' : 's'} will go too.`
            : ''),
        confirmLabel: 'Delete forever',
        danger: true,
      })
    )
      purge.mutate(list);
  }

  const toggle = (item: TrashItem) => {
    const next = new Set(selected);
    const k = keyOf(item);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setSelected(next);
  };

  if (query.isLoading) return <LoadingState label="Loading trash…" />;
  if (query.isError) {
    return <ErrorState message="Could not load the trash." onRetry={() => void query.refetch()} />;
  }

  const busy = restore.isPending || purge.isPending;

  return (
    <>
      <p className="mb-3 text-sm text-ink-muted">
        Deleted campaigns and posts are kept here for {TRASH_RETENTION_DAYS} days, then permanently
        deleted automatically. Restoring puts an item back exactly where it was.
      </p>

      {items.length === 0 ? (
        <EmptyState
          title="Trash is empty"
          description={`Deleted campaigns and posts appear here for ${TRASH_RETENTION_DAYS} days before they are cleared.`}
        />
      ) : (
        <div className="rounded-xl border border-line bg-card">
          <div className="flex flex-wrap items-center gap-3 border-b border-line-soft px-4 py-2.5">
            <label className="flex items-center gap-2 text-sm text-ink-label">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => setSelected(allSelected ? new Set() : new Set(items.map(keyOf)))}
                className="h-4 w-4 rounded border-line-input"
              />
              {selectedItems.length > 0 ? `${selectedItems.length} selected` : 'Select all'}
            </label>
            {selectedItems.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => restore.mutate(selectedItems)}
                >
                  <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden />
                  Restore
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={busy}
                  onClick={() => void askPurge(selectedItems)}
                >
                  <Trash2 className="mr-1.5 h-4 w-4" aria-hidden />
                  Delete
                </Button>
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="focus-ring rounded text-sm font-medium text-ink-muted hover:text-ink"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          <ul>
            {items.map((item) => {
              const left = daysLeft(item.purgeAt);
              const TypeIcon =
                item.kind === 'campaign'
                  ? FolderKanban
                  : postTypeIcon[item.channel ?? ContentChannel.BLOG];
              return (
                <li
                  key={keyOf(item)}
                  className="flex flex-wrap items-center gap-3 border-b border-line-soft px-4 py-3 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(keyOf(item))}
                    onChange={() => toggle(item)}
                    className="h-4 w-4 shrink-0 rounded border-line-input"
                    aria-label={`Select ${item.title}`}
                  />
                  <TypeIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{item.title}</p>
                    {/* A post's scheduling category follows its line, beside the text rather than
                        inside it, where the text's ellipsis would cut it off. When the two do not
                        fit side by side the pill moves under the text, whole (a wrapping row moves
                        the item that does not fit), as the campaign card's does; the text then
                        shortens on its own line. */}
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 overflow-hidden">
                      <p className="min-w-0 truncate text-xs text-ink-faint">
                        {item.kind === 'campaign'
                          ? `Campaign · ${item.postCount ?? 0} post${item.postCount === 1 ? '' : 's'} restored with it`
                          : `Post${item.campaignName ? ` · in ${item.campaignName}` : ''}`}
                        {' · deleted '}
                        {formatRelative(item.deletedAt)}
                      </p>
                      {item.kind === 'post' && (
                        <>
                          <span className="sr-only">Scheduling category: </span>
                          <CategoryPill category={item.category} />
                        </>
                      )}
                    </div>
                  </div>
                  {item.kind === 'post' && item.platform && (
                    <PlatformIcon platform={item.platform} />
                  )}
                  {/* Who created the post: the name is the picture's label and its hover tooltip. */}
                  {item.kind === 'post' && (
                    <>
                      <span className="sr-only">Created by</span>
                      <PostCreatorAvatar person={item.createdBy} size="xs22" />
                    </>
                  )}
                  <span
                    className={cn(
                      'shrink-0 text-xs',
                      left <= 3 ? 'font-medium text-red-600 dark:text-error-dark' : 'text-ink-faint',
                    )}
                  >
                    {left === 0 ? 'Deletes today' : `${left} day${left === 1 ? '' : 's'} left`}
                  </span>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => restore.mutate([item])}
                      className="focus-ring rounded-md px-2 py-1 text-sm font-medium text-link hover:bg-tint disabled:opacity-50"
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void askPurge([item])}
                      className="focus-ring rounded-md px-2 py-1 text-sm font-medium text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}
